import { React, useState, useEffect, useMemo } from "react";
import { Typography, Box } from "@mui/material";
import RDataGrid from "../reusables/RDataGrid";
import PageLoader from "../loader/PageLoader";
import EditIcon from "@mui/icons-material/Edit";
import moment from "moment";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import useAuth from "../hooks/useAuth";
import AddPopup from "../popup/AddPopup";
import ReorderIcon from "@mui/icons-material/Reorder";
import { GridActionsCellItem } from "@mui/x-data-grid";
import Add from "@mui/icons-material/Add";
import Button from "@mui/joy/Button";
import Notification from "../popup/Notification";
import ConfirmDialog from "../dialog/ConfirmDialog";
import AddSupplier from "../forms/AddSupplier";
import { LightTooltip } from "../tooltip/ToolTip";
import { date_format2, time_format } from "../constants/AppConstants";

const Customers = () => {
  const [isFetchingSuppliers, setIsFetchingSuppliers] = useState(true);
  const [editSupplier, setEditSupplier] = useState(false);
  const [viewSupplier, setViewSupplier] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [refreshSuppliers, setSetRefreshSuppliers] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });

  const [suppliers, setSuppliers] = useState([]);
  const CUSTOMERS_URL = "api/v1/customers/tenant/";
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();

  const divStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  };

  const onDelete = async (id) => {
    const controller = new AbortController();
    setConfirmDialog({ ...confirmDialog, isOpen: false });

    try {
      const response = await axiosPrivate.delete(CUSTOMERS_URL + "/" + id, {
        signal: controller.signal,
      });
      var message = response.data.message;
      setNotify({
        isOpen: true,
        message: message,
        type: "success",
      });
      setSetRefreshSuppliers(new Date());
    } catch (error) {
      var errorMessage = "";
      if (error.response) {
        // The client was given an error response (5xx, 4xx)
        const errorData = error.response.data;
        errorMessage = errorData.message;
      } else if (error.request) {
        // The client never received a response, and the request was never left
        errorMessage = "No server response";
      } else {
        // Anything else
        errorMessage = "Unexpected error occurred";
      }
      setConfirmDialog({
        isOpen: true,
        title: "Failed To Delete Supplier",
        subTitle: errorMessage,
        action: "Retry",
        onConfirm: () => {
          onDelete(id);
        },
      });
    }
  };

  useEffect(() => {
    if (!isFetchingSuppliers) {
      setIsFetchingSuppliers(true);
    }

    let isMounted = true;
    const controller = new AbortController();

    const getJobs = async () => {
      try {
        const response = await axiosPrivate.get(
          `${CUSTOMERS_URL}${auth.tenantId}`,
          {
            signal: controller.signal,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        isMounted && setSuppliers(response.data.data);
        setIsFetchingSuppliers(false);
      } catch (error) {
        var errorMessage = "";
        if (error.response) {
          // The client was given an error response (5xx, 4xx)
          const errorData = error.response.data;
          errorMessage = errorData.message;
        } else if (error.request) {
          // The client never received a response, and the request was never left
          errorMessage = "No server response";
        } else {
          // Anything else
          errorMessage = "Unexpected error occurred";
        }
        console.log(errorMessage);
        setIsFetchingSuppliers(false);
      }
    };

    getJobs();

    return () => {
      isMounted = false;
      controller.abort();
    };

    /* eslint-disable react-hooks/exhaustive-deps */
  }, [refreshSuppliers]);

  const columns = useMemo(
    () => [
      {
        field: "rowNo", // from DataGrid rows defifnition
        headerName: "No.",
        sortable: false,
        headerClassName: "super-app-theme--header",
        minWidth: 87,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
      },
      {
        field: "name",
        headerName: "Name",
        headerClassName: "super-app-theme--header",
        minWidth: 160,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
      },
      {
        field: "phone",
        headerName: "Phone",
        headerClassName: "super-app-theme--header",
        minWidth: 140,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
      },
      {
        field: "timeStamp",
        headerName: "Date Created",
        headerClassName: "super-app-theme--header",
        minWidth: 200,
        flex: 1,
        //align: "center",
        //headerAlign: "center",
        align: "left",
        headerAlign: "left",
        editable: false,
        renderCell: function (params) {
          const timestamp = params.row.timeStamp;
          var date = moment(timestamp).format(date_format2);
          var time = moment(timestamp).format(time_format);
          return `${date} at ${time}`;
        },
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        headerClassName: "super-app-theme--header",
        align: "center",
        headerAlign: "center",
        minWidth: 120,
        flex: 1,
        sortable: false,
        disableClickEventBubbling: true,
        cellClassName: "actions",
        getActions: (params) => {
          return [
            <LightTooltip title="Edit customer">
              <GridActionsCellItem
                icon={<EditIcon />}
                label="Edit"
                className="textPrimary"
                onClick={function () {
                  const currentRow = params.row;
                  const selectedSupplier = JSON.stringify(currentRow, null, 4);
                  setViewSupplier(false);
                  setEditSupplier(true);
                  setSelectedSupplier(selectedSupplier);
                  return setOpenPopup(true);
                }}
                color="success"
              />
            </LightTooltip>,
            <LightTooltip title="Delete customer">
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                color="error"
                onClick={function () {
                  setConfirmDialog({
                    isOpen: true,
                    title: "Are you sure you want to delete this customer?",
                    subTitle: "You can't undo this operation",
                    action: "Delete",
                    onConfirm: () => {
                      onDelete(params.row.id);
                    },
                  });
                }}
              />
            </LightTooltip>,
            <GridActionsCellItem
              icon={<ReorderIcon />}
              label={"View Customer"}
              onClick={function () {
                const currentRow = params.row;
                const selectedSupplier = JSON.stringify(currentRow, null, 4);
                setViewSupplier(true);
                setEditSupplier(true); // You might want to edit supplier under view supplier
                setSelectedSupplier(selectedSupplier);
                return setOpenPopup(true);
              }}
              color="inherit"
              showInMenu
            />,
          ];
        },
      },
    ],
    []
  );

  // Close dialog upon successful supplier add/update
  const closeDialog = (message) => {
    setOpenPopup(false);
    setNotify({
      isOpen: true,
      message: message,
      type: "success",
    });
    setSetRefreshSuppliers(new Date());
  };

  return isFetchingSuppliers ? (
    <PageLoader title="Loading customers..." />
  ) : (
    <Box
      sx={{
        height: "100%",
        width: "100%",
      }}
    >
      {suppliers?.length ? (
        <Box
          sx={{
            height: "100%",
            width: "100%",
          }}
        >
          <div style={divStyle}>
            <Typography
              sx={{
                textAlign: "left",
                mt: 3,
                mb: 3,
                fontSize: {
                  lg: 30,
                  md: 20,
                  sm: 20,
                  xs: 19,
                },
              }}
            >
              Customers
            </Typography>

            {/* <Button
              sx={{
                size: { xs: "xs", sm: "xs", md: "md", lg: "lg", xl: "lg" },
              }}
              variant="solid"
              color="primary"
              onClick={function () {
                setEditSupplier(false);
                setSelectedSupplier(null);
                setOpenPopup(true);
              }}
              startDecorator={<Add />}
            >
              Customer
            </Button> */}
          </div>
          <Box
            sx={{
              width: "100%",
              margin: "0 auto 16px",
              height: 560,
              overflow: "hidden",
            }}
          >
            <RDataGrid columns={columns} rows={suppliers} rowHeight={63} />
          </Box>
          <Notification notify={notify} setNotify={setNotify} />
          <ConfirmDialog
            confirmDialog={confirmDialog}
            setConfirmDialog={setConfirmDialog}
          />
          <AddPopup
            width="md"
            openPopup={openPopup}
            setOpenPopup={setOpenPopup}
            title={
              viewSupplier
                ? "View (Edit) Customer"
                : editSupplier
                ? "Edit (View) Customer"
                : "Add Customer"
            }
          >
            <AddSupplier
              closeDialog={closeDialog}
              editSupplier={editSupplier}
              selectedSupplier={selectedSupplier}
            />
          </AddPopup>
        </Box>
      ) : (
        <div style={divStyle}>
          <Typography
            variant="h4"
            component="h4"
            sx={{ textAlign: "center", mt: 3, mb: 3 }}
          >
            No customers to display
          </Typography>
          {/* <Button
            sx={{
              size: { xs: "xs", sm: "xs", md: "md", lg: "lg", xl: "lg" },
            }}
            variant="solid"
            color="primary"
            onClick={function () {
              setEditSupplier(false);
              setSelectedSupplier(null);
              setOpenPopup(true);
            }}
            startDecorator={<Add />}
          >
            Customer
          </Button> */}
          <Notification notify={notify} setNotify={setNotify} />
          <ConfirmDialog
            confirmDialog={confirmDialog}
            setConfirmDialog={setConfirmDialog}
          />
          <AddPopup
            width="md"
            openPopup={openPopup}
            setOpenPopup={setOpenPopup}
            title={
              viewSupplier
                ? "View (Edit) Customer"
                : editSupplier
                ? "Edit (View) Customer"
                : "Add Customer"
            }
          >
            <AddSupplier
              closeDialog={closeDialog}
              editSupplier={editSupplier}
              selectedSupplier={selectedSupplier}
            />
          </AddPopup>
        </div>
      )}
    </Box>
  );
};

export default Customers;
